/* import __COLOCATED_TEMPLATE__ from './folder-title.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type Folder from 'embercom/models/content-service/folder';
import { type HighlightedResult } from 'embercom/objects/inbox/knowledge-base/search-result';
import { tracked } from 'tracked-built-ins';
import type IntlService from 'ember-intl/services/intl';
import { inject as service } from '@ember/service';

export interface Args {
  folder?: Folder;
  title?: string;
  showIcon?: boolean;
  createFolder?: boolean;
  truncateShort?: boolean;
  truncateLong?: boolean;
  isHeader?: boolean;
  highlightedTitleArray?: HighlightedResult[];
  isDummyArticlesFolder?: boolean;
  // Routes for fodler navigation. Passed down from route templates
  // to keep component decoupled from router. Defaults to Knowledge Hub if not provided.
  parentFolderRoute?: string;
  allContentRoute?: string;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
  Blocks: {
    default: [];
  };
}

export default class FolderTitle extends Component<Signature> {
  @tracked hasTruncatedText = true;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  get tooltipText() {
    if (this.args.folder?.get('isAiGenerated')) {
      return this.intl.t('knowledge-hub.conversational.conversation-extraction-settings.title');
    }
    return this.args?.title || this.args.folder?.get('title') || '';
  }

  get shouldShowBreadcrumb(): boolean {
    return !!this.args.isHeader && this.useV2Components && !this.appService.app.canUseStandalone;
  }

  @action handleMouseOver(event: MouseEvent) {
    this.hasTruncatedText = this.isTitleTruncated(event.target as HTMLElement);
  }

  isTitleTruncated(folder: HTMLElement) {
    let folderTitle = folder?.querySelector<HTMLElement>('[data-folder-title]');
    return folderTitle !== null && folderTitle?.scrollWidth > folderTitle?.offsetWidth;
  }

  get defaultFolderIcon() {
    if (this.args.folder?.get('isAiGenerated')) {
      return 'ai';
    } else {
      return this.useV2Components && this.args.isDummyArticlesFolder ? 'campaign' : 'folder';
    }
  }

  get useV2Components() {
    return this.appService.app.canUseUnifiedContentUI;
  }

  get parentFolderTitle() {
    let parentFolder = this.args.folder?.get('parentFolder');
    if (parentFolder) {
      return parentFolder.get('title');
    } else {
      return this.args.folder?.isSynced
        ? this.intl.t('knowledge-hub.folders.heading')
        : this.intl.t('knowledge-hub.folders.native-content-header');
    }
  }

  get parentFolderObject() {
    return this.args.folder?.get('parentFolder');
  }

  // Returns the route for navigating to folders.
  // Uses the route passed via @parentFolderRoute, or defaults to Knowledge Hub.
  // Architecture: Routes are determined at the route template level (which knows
  // the context) and passed down through the component hierarchy.
  get allContentRoute(): string {
    return this.args.allContentRoute ?? 'apps.app.knowledge-hub';
  }

  // Returns the route for navigating to all content.
  // Uses the route passed via @allContentRoute, or defaults to Knowledge Hub.
  // Architecture: Routes are determined at the route template level (which knows
  // the context) and passed down through the component hierarchy.
  get parentFolderRoute(): string {
    return this.args.parentFolderRoute ?? 'apps.app.knowledge-hub.folder';
  }

  get nativeContentSubtitle(): string {
    return this.appService.app.canUseStandalone
      ? this.intl.t('knowledge-hub.folders.native-content-subtitle-standalone')
      : this.intl.t('knowledge-hub.folders.native-content-subtitle');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Folders::FolderTitle': typeof FolderTitle;
  }
}
